<script setup>
import axios from 'axios';
import { BCard, BFormSelect, BFormCheckbox } from 'bootstrap-vue-next';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import ErrorState from '~/common/components/ErrorState.vue';
import { booleanParam } from '~/common/QueryParams';
import { useSeriesChartQuery } from '~/common/useSeriesChartQuery';

import DashboardChart from './dashboard-page/DashboardChart.vue';

const props = defineProps({
  allSeries: {
    type: Array,
    default: null,
  },
  seriesLabels: {
    type: Object,
    default: null,
  },
});

const data = ref([]);
const error = ref(null);
const chartRef = ref(null);

const route = useRoute();
const platform = route.meta.platform || route.params.platform;
const pageTitle = route.meta.title;

const allSeries = computed(() => {
  if (props.allSeries) {
    return props.allSeries;
  }

  if (data.value.length === 0) {
    return [];
  }

  return Object.keys(data.value[0]).filter(k => k != 'activity_date');
});
const seriesLabels = computed(() => {
  if (props.seriesLabels) {
    return props.seriesLabels;
  }

  if (data.value.length === 0) {
    return {};
  }

  return Object.fromEntries(
    Object.keys(data.value[0])
      .filter(k => k != 'activity_date')
      .map(k => [k, k]),
  );
});

const {
  period,
  chartType,
  stacked,
  unique,
  extras: { widerCommunity },
  updateQueryString,
  updateActiveSeries,
  resetActiveSeries,
  hideInactiveSeries,
} = useSeriesChartQuery({
  allSeries,
  extraParams: { widerCommunity: { param: booleanParam(false) } },
});

const fetchData = async () => {
  const url = '/api/v1/activity_dashboard';

  error.value = null;
  let response;

  try {
    response = await axios.get(url, {
      params: {
        platform,
        period: period.value,
        unique: unique.value,
        wider_community: widerCommunity.value,
      },
    });
  } catch (e) {
    error.value = e;
  }

  if (response?.status === 200) {
    data.value = await response.data;
  }
};

const graphDates = computed(() => data.value?.map(activity => activity.activity_date));

const graphPoints = computed(() => {
  if (!graphDates.value?.length) {
    return [];
  }

  return graphDates.value.map((date) => {
    const dayData = data.value.find(activity => activity.activity_date === date);

    return Object.fromEntries(
      allSeries.value.map(series => [series, dayData?.[series] || 0]),
    );
  });
});

watch([period, unique, widerCommunity], () => {
  fetchData();
  updateQueryString();
});

watch(allSeries, resetActiveSeries);

watch([data, chartRef, chartType], () => {
  if (data.value.length && chartRef.value) {
    hideInactiveSeries(chartRef.value);
  }
}, { flush: 'post' });

fetchData();
</script>

<template>
  <ErrorState v-if="error" />
  <div v-else>
    <h2 class="my-3">{{ pageTitle }}</h2>

    <BCard class="mb-4">
      <div class="d-flex flex-wrap gap-4 align-items-center">
        <div class="d-flex align-items-center">
          <label class="form-label me-2 mb-0">Period:</label>
          <BFormSelect
            v-model="period"
            size="sm"
            class="select-sm-fixed"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </BFormSelect>
        </div>

        <div class="d-flex align-items-center">
          <label class="form-label me-2 mb-0">Chart Type:</label>
          <BFormSelect
            v-model="chartType"
            size="sm"
            class="select-sm-fixed"
          >
            <option value="line">Line</option>
            <option value="bar">Bar</option>
          </BFormSelect>
        </div>

        <div class="d-flex align-items-center">
          <BFormCheckbox
            v-model="stacked"
            :unchecked-value="false"
            data-testid="stacked-checkbox"
            class="mb-0"
          >
            Stacked
          </BFormCheckbox>
        </div>

        <div class="d-flex align-items-center">
          <BFormCheckbox
            v-model="unique"
            :unchecked-value="false"
            data-testid="unique-checkbox"
            class="mb-0"
          >
            Unique Contributors
          </BFormCheckbox>
        </div>

        <div class="d-flex align-items-center">
          <BFormCheckbox
            v-model="widerCommunity"
            :unchecked-value="false"
            data-testid="wider-community-checkbox"
            class="mb-0"
          >
            Wider Community
          </BFormCheckbox>
        </div>
      </div>
    </BCard>

    <DashboardChart
      v-if="data.length"
      ref="chartRef"
      :graph-points="graphPoints"
      :graph-dates="graphDates"
      :chart-type="chartType"
      :stacked="stacked"
      :all-series="allSeries"
      :series-labels="seriesLabels"
      @update:active-series="updateActiveSeries"
    />
  </div>
</template>
